import React from 'react';
import PropTypes from 'prop-types';
import { useNode } from '@splashthat/splash-cms-engine';
import { BRAND_LIBRARY_DARK_TEXT_COLOR } from 'common/constants/brandLibrary';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { ScreenReaderSpan } from 'common/components/TouchpointEditor/components/TouchpointContainer';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';
import { BlockContainer } from './Block.styles';

const BASE_CLASS_NAME = 'Block';
export default function Block({
    children,
    className,
    hovered,
    id,
    selected,
    styles: { backgroundColor, backgroundImageUrl },
    meta: { accessibilityText },
}) {
    const { theme } = useTouchpointData();
    const node = useNode();
    const customCssClassName = getCustomCssClassName(node);
    const joinedClassNames = [className, customCssClassName || ''].join(' ');

    return (
        <BlockContainer
            id={id}
            selected={selected}
            hovered={hovered}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
            onClick={node.actions.onClick()}
            data-qa={`${BASE_CLASS_NAME}__${id}--container`}
            $backgroundColor={theme.colors[backgroundColor]}
            $backgroundImageUrl={backgroundImageUrl}
            className={joinedClassNames}
        >
            {/* decorative bg: hidden from AT now; swap aria-hidden for role="img" aria-label={accessibilityText} when image becomes meaningful */}
            {!!accessibilityText && <ScreenReaderSpan aria-hidden="true" />}
            {children}
        </BlockContainer>
    );
}

Block.splash = {
    related: {
        settings: React.lazy(() => import('./Block.tools')),
    },
    defaultProps: {
        styles: {
            backgroundColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            backgroundImageUrl: '',
        },
        meta: {
            accessibilityText: '',
        },
    },
};

Block.propTypes = {
    children: PropTypes.node,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    styles: PropTypes.shape({
        backgroundColor: PropTypes.number,
        backgroundImageUrl: PropTypes.string,
    }).isRequired,
    meta: PropTypes.shape({
        accessibilityText: PropTypes.string,
    }).isRequired,
    id: PropTypes.string.isRequired,
    className: PropTypes.string,
};

Block.defaultProps = {
    children: null,
    className: '',
};
